import React, { Key, useEffect, useMemo, useState } from 'react';
import { Layout, Spin } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import { DataNode } from 'antd/lib/tree';
import {
  Button,
  DatePicker,
  Empty,
  FormTitle,
  Input,
  Tree,
  useBreadcrumbRoutes,
  Wrapper,
  EchartsWithTheme,
  useTheme,
  Tooltip,
} from '@maxtropy/components';
import {
  apiV2EnergyResilienceAnalysisTreePost,
  apiV2EnergyResilienceAnalysisDetailPost,
  apiV2EnergyResilienceAnalysisTrendPost,
} from '@maxtropy/device-customer-apis-v2';
import DragResize from '@/components/DragResize';
import { keepPercentOrLine, keepTwoOrLine } from '@/utils/utils';
import { buildTrendOption } from './chartOptions';
import RecalculateModal from './RecalculateModal';
import styles from './index.module.scss';
import type {
  Dimension,
  EnergyResilienceAnalysisDetailResponse,
  EnergyResilienceAnalysisRequest,
  EnergyResilienceAnalysisTreeNode,
} from './types';

const { Content, Sider } = Layout;
const { RangePicker } = DatePicker;

type TreeApiNode = EnergyResilienceAnalysisTreeNode;
type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface AnalysisNodeMeta {
  key: string;
  configId?: number | string;
  name?: string;
  mediumType?: number | string;
  dimension?: Dimension;
  level: number;
  parentKeys: string[];
  pathNames: string[];
}

interface AnalysisTreeNode extends DataNode {
  children?: AnalysisTreeNode[];
  meta: AnalysisNodeMeta;
}

interface RouteDefaultParams {
  configId?: string;
  dimension?: Dimension;
  mediumType?: string;
}

const RANGE_CONFIG = [
  { range: 'low', label: '负载率: 0~60%', color: '#18d58f' },
  { range: 'mid', label: '负载率: 60~80%', color: '#f3c84b' },
  { range: 'high', label: '负载率: 80~100%', color: '#ff8a3d' },
  { range: 'over', label: '负载率: 100%以上', color: '#ff4d5f' },
];

const getQueryParams = (search: string) => new URLSearchParams(search);

const formatDuration = (minutes?: number | string) => {
  const value = Number(minutes);
  if (minutes === undefined || minutes === null || Number.isNaN(value)) return '--';
  if (value > 60) return `${keepTwoOrLine(value / 60)}小时`;
  return `${keepTwoOrLine(value)}分钟`;
};

const formatPercentRatio = (value?: number | string) => {
  const numberValue = Number(value);
  if (value === undefined || value === null || Number.isNaN(numberValue)) return '--';
  return `${keepPercentOrLine(numberValue)}%`;
};

const normalizeDimension = (dimension?: string | null) => dimension?.toLowerCase() as Dimension | undefined;

const isLeafNode = (node: AnalysisTreeNode) => !node.children || node.children.length === 0;

const isSelectableNode = (node: AnalysisTreeNode) => node.meta.level === 3;

const sortThirdLevel = (nodes: AnalysisTreeNode[]): AnalysisTreeNode[] =>
  nodes.map(node => {
    if (!node.children?.length) return node;
    const children = sortThirdLevel(node.children);
    return {
      ...node,
      children:
        children[0]?.meta?.level === 3
          ? [...children].sort((a, b) => String(a.title ?? '').localeCompare(String(b.title ?? ''), 'zh-Hans-CN'))
          : children,
    };
  });

const transformTree = (
  nodes: TreeApiNode[] = [],
  parentMeta?: Pick<AnalysisNodeMeta, 'mediumType' | 'dimension' | 'parentKeys' | 'pathNames'>,
  level = 1
): AnalysisTreeNode[] => {
  return nodes.map((item, index) => {
    const mediumType = item.mediumType ?? parentMeta?.mediumType;
    const dimension = normalizeDimension(item.dimension) ?? parentMeta?.dimension;
    const rawId = item.id ?? `${level}-${index}`;
    const key = [mediumType ?? 'medium', dimension ?? 'root', rawId, level, index].join('-');
    const pathNames = [...(parentMeta?.pathNames ?? []), item.name ?? '--'];
    const parentKeys = parentMeta?.parentKeys ?? [];
    const meta: AnalysisNodeMeta = {
      key,
      configId: level === 3 ? item.id : undefined,
      name: item.name,
      mediumType,
      dimension,
      level,
      parentKeys,
      pathNames,
    };
    const children = transformTree(
      item.children ?? [],
      {
        mediumType,
        dimension,
        parentKeys: [...parentKeys, key],
        pathNames,
      },
      level + 1
    );

    return {
      key,
      title: item.name ?? '--',
      selectable: level === 3,
      children,
      meta,
    };
  });
};

const flattenTree = (nodes: AnalysisTreeNode[]) => {
  const list: AnalysisTreeNode[] = [];
  const loop = (items: AnalysisTreeNode[]) => {
    items.forEach(item => {
      list.push(item);
      if (item.children?.length) loop(item.children);
    });
  };
  loop(nodes);
  return list;
};

const uniqueKeys = (keys: Key[]) => Array.from(new Set(keys));

const filterTreeByKeyword = (nodes: AnalysisTreeNode[], keyword: string): AnalysisTreeNode[] => {
  const searchValue = keyword.trim();
  if (!searchValue) return nodes;

  return nodes
    .map(node => {
      const children = node.children ? filterTreeByKeyword(node.children, searchValue) : [];
      const match = isLeafNode(node) && String(node.title ?? '').includes(searchValue);
      if (match || children.length) {
        return { ...node, children };
      }
      return null;
    })
    .filter(Boolean) as AnalysisTreeNode[];
};

const getRouteDefaultParams = (query: URLSearchParams): RouteDefaultParams => ({
  configId: query.get('configId') ?? query.get('objectId') ?? undefined,
  dimension: normalizeDimension(query.get('dimension') ?? query.get('tabType')),
  mediumType: query.get('mediumType') ?? query.get('energyType') ?? undefined,
});

// 进入某个分组后，默认取树顺序里的第一个可查看节点。
const findFirstSelectableNode = (nodes: AnalysisTreeNode[]): AnalysisTreeNode | undefined => {
  for (const node of nodes) {
    const childTarget = node.children?.length ? findFirstSelectableNode(node.children) : undefined;
    if (childTarget) return childTarget;
    if (isSelectableNode(node)) return node;
  }
  return undefined;
};

// 先定位到路由指定的维度分组，再从该分组里选择默认数据。
const findDimensionNode = (
  nodes: AnalysisTreeNode[],
  routeParams: Pick<RouteDefaultParams, 'dimension' | 'mediumType'>
): AnalysisTreeNode | undefined => {
  const { dimension, mediumType } = routeParams;

  for (const node of nodes) {
    const sameDimension = dimension ? node.meta.dimension === dimension : true;
    const sameMediumType = mediumType ? String(node.meta.mediumType) === mediumType : true;
    if (sameDimension && sameMediumType) return node;

    const childTarget = node.children?.length ? findDimensionNode(node.children, routeParams) : undefined;
    if (childTarget) return childTarget;
  }

  return undefined;
};

// 指定维度不存在时，退回到能源介质分组下的第一个维度。
const findMediumNode = (nodes: AnalysisTreeNode[], mediumType?: string): AnalysisTreeNode | undefined => {
  if (!mediumType) return undefined;

  for (const node of nodes) {
    if (String(node.meta.mediumType) === mediumType) return node;

    const childTarget = node.children?.length ? findMediumNode(node.children, mediumType) : undefined;
    if (childTarget) return childTarget;
  }

  return undefined;
};

const findDefaultNode = (tree: AnalysisTreeNode[], routeParams: RouteDefaultParams) => {
  const selectableNodes = flattenTree(tree).filter(isSelectableNode);
  const { configId, dimension, mediumType } = routeParams;

  // 选择优先级：精确 configId > mediumType + dimension > mediumType > 全树第一个可选节点。
  if (configId) {
    const target = selectableNodes.find(item => {
      const sameConfigId = String(item.meta.configId) === configId;
      const sameDimension = dimension ? item.meta.dimension === dimension : true;
      const sameMediumType = mediumType ? String(item.meta.mediumType) === mediumType : true;
      return sameConfigId && sameDimension && sameMediumType;
    });
    if (target) return target;
  }

  if (dimension || mediumType) {
    const dimensionNode = findDimensionNode(tree, { dimension, mediumType });
    const target = dimensionNode
      ? findFirstSelectableNode(dimensionNode.children ?? []) ??
        (isSelectableNode(dimensionNode) ? dimensionNode : undefined)
      : undefined;
    if (target) return target;
  }

  if (mediumType) {
    const mediumNode = findMediumNode(tree, mediumType);
    const target = mediumNode
      ? findFirstSelectableNode(mediumNode.children ?? []) ?? (isSelectableNode(mediumNode) ? mediumNode : undefined)
      : undefined;
    if (target) return target;
  }

  return selectableNodes[0];
};

const getSearchExpandedKeys = (tree: AnalysisTreeNode[]) =>
  uniqueKeys(flattenTree(tree).flatMap(item => (isSelectableNode(item) ? item.meta.parentKeys : [])));

const getMetricLabels = (node?: AnalysisNodeMeta) => {
  if (node?.dimension === 'line') {
    return { realtime: '实时电流(A)', rated: '额定电流(A)' };
  }
  if (node?.dimension === 'station' && String(node?.mediumType) === '0') {
    return { realtime: '实时功率(kW)', rated: '变压器容量(kW)' };
  }
  return { realtime: '实时流速（Nm³/h）', rated: '额定流速（Nm³/h）' };
};

const EnergyResilienceAnalysis = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const { search } = useLocation();
  const query = useMemo(() => getQueryParams(search), [search]);
  const routeDefaultParams = useMemo(() => getRouteDefaultParams(query), [query]);
  const [openSider, setOpenSider] = useState(true);
  const [siderWidth, setSiderWidth] = useState(260);
  const [dragStatus, setDragStatus] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [treeData, setTreeData] = useState<AnalysisTreeNode[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [searchExpandedKeys, setSearchExpandedKeys] = useState<Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [selectedNode, setSelectedNode] = useState<AnalysisNodeMeta>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => {
    const initialDate = dayjs(query.get('date') || undefined);
    return [initialDate, initialDate];
  });
  const [pickerDates, setPickerDates] = useState<RangeValue>(null);
  const [calcModalOpen, setCalcModalOpen] = useState(false);
  const theme = useTheme();

  const disabledAnalysisDate = (current: Dayjs) => {
    const selectedDate = pickerDates?.[0] ?? pickerDates?.[1];
    const exceedsSevenDays =
      selectedDate && Math.abs(current.startOf('day').diff(selectedDate.startOf('day'), 'days')) >= 7;
    return !!exceedsSevenDays || current.isAfter(dayjs().endOf('day'));
  };

  const { loading: treeLoading } = useRequest(
    () =>
      apiV2EnergyResilienceAnalysisTreePost({}).then(res =>
        sortThirdLevel(transformTree((res?.list ?? []) as TreeApiNode[]))
      ),
    {
      onSuccess: data => {
        setTreeData(data);
      },
    }
  );

  useEffect(() => {
    if (!treeData.length) return;
    const defaultNode = findDefaultNode(treeData, routeDefaultParams);
    if (defaultNode) {
      setSelectedKeys([defaultNode.key]);
      setSelectedNode(defaultNode.meta);
      setExpandedKeys(defaultNode.meta.parentKeys);
    }
  }, [routeDefaultParams, treeData]);

  useEffect(() => {
    const routeDate = query.get('date');
    if (routeDate) {
      const nextDate = dayjs(routeDate);
      setDateRange([nextDate, nextDate]);
    }
  }, [query]);

  const requestParams = useMemo<EnergyResilienceAnalysisRequest | undefined>(
    () =>
      selectedNode
        ? {
            mediumType: selectedNode.mediumType,
            dimension: selectedNode.dimension,
            configId: selectedNode.configId,
            startTime: dateRange[0].startOf('day').valueOf(),
            endTime: dateRange[1].endOf('day').valueOf(),
          }
        : undefined,
    [dateRange, selectedNode]
  );

  const {
    data: detailData,
    loading: detailLoading,
    refresh: refreshDetail,
  } = useRequest(() => apiV2EnergyResilienceAnalysisDetailPost(requestParams as EnergyResilienceAnalysisRequest), {
    ready: !!requestParams,
    refreshDeps: [requestParams],
  });

  const {
    data: trendData,
    loading: trendLoading,
    refresh: refreshTrend,
  } = useRequest(() => apiV2EnergyResilienceAnalysisTrendPost(requestParams as EnergyResilienceAnalysisRequest), {
    ready: !!requestParams,
    refreshDeps: [requestParams],
  });

  const filteredTreeData = useMemo(() => filterTreeByKeyword(treeData, keyword), [keyword, treeData]);
  const isSearching = !!keyword.trim();
  const isToday = dateRange[0].isSame(dayjs(), 'day');
  const metricLabels = useMemo(() => getMetricLabels(selectedNode), [selectedNode]);
  const chartOption = useMemo(() => buildTrendOption(trendData, detailData), [detailData, trendData]);

  useEffect(() => {
    if (isSearching) {
      setSearchExpandedKeys(getSearchExpandedKeys(filteredTreeData));
      return;
    }
    setExpandedKeys(prevKeys => uniqueKeys([...prevKeys, ...(selectedNode?.parentKeys ?? [])]));
  }, [filteredTreeData, isSearching, selectedNode?.parentKeys]);

  const handleSelect = (keys: Key[], info: any) => {
    const node = info?.node as AnalysisTreeNode | undefined;
    if (!node?.meta || !isSelectableNode(node)) return;
    setSelectedKeys(keys);
    setSelectedNode(node.meta);
    if (!isSearching) {
      setExpandedKeys(prevKeys => uniqueKeys([...prevKeys, ...node.meta.parentKeys]));
    }
  };

  const dragChange = (width: number) => {
    if (openSider) {
      setSiderWidth(width);
    }
  };

  const rangeMap = useMemo(() => {
    const map = new Map<string, NonNullable<EnergyResilienceAnalysisDetailResponse['rangeDurations']>[number]>();
    detailData?.rangeDurations?.forEach(item => {
      if (item.range) map.set(item.range, item);
    });
    return map;
  }, [detailData?.rangeDurations]);

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} hasPadding={false}>
      <Layout className={styles.layout}>
        <Sider
          width={openSider ? siderWidth : 0}
          className={styles.sider}
          style={{ transition: dragStatus ? 'none' : 'all 0.2s' }}
        >
          <DragResize init={260} dragChange={dragChange} dragStatus={setDragStatus} />
          <div className={styles.siderInner}>
            <Input.Search
              placeholder="请输入"
              allowClear
              value={keyword}
              onChange={event => setKeyword(event.target.value)}
              onSearch={setKeyword}
            />
            <div className={styles.treeBox}>
              <Spin spinning={treeLoading}>
                {filteredTreeData?.length ? (
                  <Tree
                    blockNode
                    treeData={filteredTreeData}
                    expandedKeys={isSearching ? searchExpandedKeys : expandedKeys}
                    selectedKeys={selectedKeys}
                    onExpand={keys => (isSearching ? setSearchExpandedKeys(keys) : setExpandedKeys(keys))}
                    onSelect={handleSelect}
                    titleRender={(node: any) => <Tooltip title={node.title}>{node.title}</Tooltip>}
                  />
                ) : (
                  <Empty />
                )}
              </Spin>
            </div>
          </div>
        </Sider>
        <Content className={styles.content} style={{ marginLeft: openSider ? 10 : 0 }}>
          <div className="toggleBtn" style={{ left: openSider ? -30 : 0 }} onClick={() => setOpenSider(!openSider)}>
            {openSider ? <LeftOutlined /> : <RightOutlined />}
          </div>
          <div className={styles.rightContainer}>
            {!selectedNode ? (
              <Empty className={styles.pageEmpty} />
            ) : (
              <Spin spinning={detailLoading || trendLoading}>
                <FormTitle
                  title={selectedNode.name || '--'}
                  extraContent={
                    <div className={styles.datePicker}>
                      <Button type="primary" onClick={() => setCalcModalOpen(true)}>
                        重计算
                      </Button>
                      <span>时间选择：</span>
                      <RangePicker
                        value={dateRange}
                        allowClear={false}
                        disabledDate={disabledAnalysisDate}
                        onCalendarChange={setPickerDates}
                        onChange={value => {
                          if (value?.[0] && value[1]) {
                            setDateRange([value[0], value[1]]);
                            setPickerDates(null);
                          }
                        }}
                        onOpenChange={open => {
                          if (!open) {
                            setPickerDates(null);
                          }
                        }}
                      />
                    </div>
                  }
                />
                <div className={styles.metricGrid}>
                  {isToday && (
                    <div className={styles.metricCard}>
                      <div className={styles.metricLabel}>{metricLabels.realtime}</div>
                      <div className={styles.metricValue}>{keepTwoOrLine(detailData?.realtimeValue)}</div>
                    </div>
                  )}
                  <div className={styles.metricCard}>
                    <div className={styles.metricLabel}>{metricLabels.rated}</div>
                    <div className={styles.metricValue}>{keepTwoOrLine(detailData?.ratedValue)}</div>
                  </div>
                  {isToday && (
                    <div className={styles.metricCard}>
                      <div className={styles.metricLabel}>实时负载率</div>
                      <div className={styles.metricValue}>{keepTwoOrLine(detailData?.realtimeLoadRate)}%</div>
                    </div>
                  )}
                  <div className={styles.metricCard}>
                    <div className={styles.metricLabel}>最大负载率</div>
                    <div className={styles.metricValue}>{keepTwoOrLine(detailData?.maxLoadRate)}%</div>
                  </div>
                  <div className={styles.metricCard}>
                    <div className={styles.metricLabel}>最小负载率</div>
                    <div className={styles.metricValue}>{keepTwoOrLine(detailData?.minLoadRate)}%</div>
                  </div>
                </div>
                <div className={styles.metricGrid}>
                  {RANGE_CONFIG.map(item => {
                    const range = rangeMap.get(item.range);
                    return (
                      <div key={item.range} className={styles.rangeCard} style={{ borderTopColor: item.color }}>
                        <div className={styles.metricLabel}>{range?.rangeName || item.label}</div>
                        <div className={styles.duration}>
                          持续 <span>{formatDuration(range?.duration)}</span>
                        </div>
                        <div className={styles.ratio}>占比: {formatPercentRatio(range?.durationPercent)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.chartBox}>
                  {trendData?.points?.length ? (
                    <EchartsWithTheme option={chartOption} theme={theme} className={styles.chart} />
                  ) : (
                    <Empty className={styles.chartEmpty} />
                  )}
                </div>
              </Spin>
            )}
          </div>
        </Content>
      </Layout>
      <RecalculateModal
        open={calcModalOpen}
        onCancel={() => setCalcModalOpen(false)}
        onSuccess={() => {
          refreshDetail();
          refreshTrend();
        }}
      />
    </Wrapper>
  );
};

export default EnergyResilienceAnalysis;
